import type { ThinkingLevel } from './desktop-model.js';
import type { DesktopModelView } from './desktop-model.js';
import type { RecommendedModelsView } from './recommended-models.js';

export type FixedModelThinkingLevel = Exclude<ThinkingLevel, 'auto'>;
export type RecommendedThinkingLevel = FixedModelThinkingLevel | 'model-default';
export interface ModelThinkingMetadata {
  thinkingLevels?: readonly unknown[];
  capabilities?: { reasoning?: boolean; thinkingAuto?: { strategy?: string } };
}

export const FIXED_MODEL_THINKING_LEVELS: readonly FixedModelThinkingLevel[] =
  ['off', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'];

/** Only explicit model definitions establish support; Auto is independent. */
export function fixedModelThinkingLevels(model: ModelThinkingMetadata): FixedModelThinkingLevel[] {
  return FIXED_MODEL_THINKING_LEVELS.filter(level =>
    model.thinkingLevels?.includes(level) &&
    (model.capabilities?.reasoning !== false || level === 'off'));
}

export function supportsRecommendedThinking(model: ModelThinkingMetadata, level: RecommendedThinkingLevel): boolean {
  return level === 'model-default' || fixedModelThinkingLevels(model).includes(level);
}

export interface ModelThinkingBootstrapOptions {
  modelThinkingVersion?: 2;
}

/** Verified model-specific choices; never a provider-wide default. */
export function modelFixedThinkingPreset(modelId: string | undefined): FixedModelThinkingLevel[] | undefined {
  return /^(?:openai\/)?gpt-5\.6-(?:sol|terra|luna)$/.test(modelId?.trim() ?? '')
    ? ['off', 'low', 'medium', 'high', 'xhigh', 'max'] : undefined;
}

/** NULL normalization and adding Auto are not fixed-level metadata additions. */
export function isAdditiveThinkingLevels(before: readonly ThinkingLevel[] | undefined, after: readonly ThinkingLevel[] | undefined): boolean {
  if (!before?.length || !after?.length) return false;
  const old = new Set(before);
  const next = new Set(after);
  return next.size > old.size && before.every(level => next.has(level)) &&
    after.every(level => old.has(level) || FIXED_MODEL_THINKING_LEVELS.includes(level as FixedModelThinkingLevel));
}

export function isModelThinkingV2(value: unknown): boolean {
  return value === 2;
}

export function projectModelThinking(model: DesktopModelView, version?: unknown): DesktopModelView {
  if (isModelThinkingV2(version)) return model;
  const auto = model.capabilities.thinkingAuto;
  const { fallbackEffort, ...autoRest } = auto ?? {};
  return {
    ...model,
    ...(model.thinkingLevels ? { thinkingLevels: model.thinkingLevels.filter(level => level !== 'max') } : {}),
    capabilities: {
      ...model.capabilities,
      ...(auto ? { thinkingAuto: {
        ...autoRest,
        strategy: auto.strategy,
        supportedEfforts: auto.supportedEfforts.filter(level => level !== 'max'),
        ...(fallbackEffort && fallbackEffort !== 'max' ? { fallbackEffort } : {}),
      } } : {}),
    },
  };
}

export function projectRecommendedThinking(view: RecommendedModelsView, version?: unknown): RecommendedModelsView {
  if (isModelThinkingV2(version)) return view;
  return { ...view, steps: view.steps.map(step => {
    if (!step.available) return step;
    if (step.thinkingLevel === 'max' || step.thinkingLevel === 'model-default') {
      return { id: step.id, available: false, reason: 'not_available' };
    }
    return { ...step, model: projectModelThinking(step.model, version) };
  }) };
}
